<template>
  <button
    v-if="canAddToOrderDraft"
    class="btn btn-outline-primary btn-sm"
    type="button"
    @click="addToOrderDraft()"
    :disabled="!canAddToOrderDraft">
    <font-awesome-icon :icon="['fas', 'cart-plus']" />
    <span class="ms-1 d-none d-lg-inline">{{ $t('action.addToOrderDraft') }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useOrdersStore, useItemsStore, useSupplierItemsStore, useReplacementItemsStore } from '@/stores';
import { toast } from 'vue3-toastify';
import { useI18n } from 'vue-i18n';
import { RESTOCK_PULL_TYPE } from '@/models/Enums';
import { DraftOrderLineFields } from '@/models/requests/OrderCreateRequest';

const i18n = useI18n();
const ordersStore = useOrdersStore();
const itemsStore = useItemsStore();
const supplierItemsStore = useSupplierItemsStore();
const replacementItemsStore = useReplacementItemsStore();

const props = defineProps({
  itemId: { type: Number, required: true },
  restockPullType: { type: Number, required: true },
  canOrderManually: { type: Boolean, required: false, default: false }
});

const canAddToOrderDraft = computed(() => {
  return props.restockPullType === RESTOCK_PULL_TYPE.onDemand || props.canOrderManually;
});

const addToOrderDraft = async () => {
  if (!canAddToOrderDraft.value) {
    return;
  }

  const item = itemsStore.getById(props.itemId);
  if (!item) {
    toast.error(i18n.t('error.itemNotFound', { itemId: props.itemId }), { autoClose: 3000 });
    return;
  }

  // Handle base items (for replacement items)
  let rootItem = item;
  const baseItemId = replacementItemsStore.getBaseItem(item.id);
  if (baseItemId) {
    rootItem = itemsStore.getById(baseItemId);
    if (!rootItem) {
      toast.error(i18n.t('error.baseItemNotFoundForItem', {
        baseItemId: baseItemId,
        itemId: item.id,
        tenantCode: item.tenant.code,
        itemCode: item.code
      }), { autoClose: 3000 });
      return;
    }
  }

  // Get the supplier item
  const supplierItem = supplierItemsStore.getByItemId(rootItem.id);
  if (!supplierItem) {
    toast.error(i18n.t('error.noSupplierFoundForItem', {
      itemId: rootItem.id,
      tenantCode: rootItem.tenant.code,
      itemCode: rootItem.code
    }), { autoClose: 3000 });
    return;
  }

  // Load current draft
  const currentDraft = await ordersStore.loadDraft() || [];

  // Check if the item already exists in the draft
  const existingItem = currentDraft.find(line => line.itemId === props.itemId);

  if (existingItem) {
    // Update quantity if already exists
    existingItem.quantity += 1;
  } else {
    // Add new item to the draft
    currentDraft.push(new DraftOrderLineFields(
      props.itemId,
      supplierItem.proposedOrderQuantity || 1,
      supplierItem.orderUnitCode
    ));
  }
  const sortedCurrentDraft = currentDraft.sort((a, b) => a.itemId - b.itemId);
  // Save the updated draft
  await ordersStore.saveDraft(
    sortedCurrentDraft,
    () => {
      toast.success(i18n.t('page.order-create.addedToOrderDraft'), { autoClose: 2000 });
    },
    (error) => {
      toast.error(i18n.t('page.order-create.addToOrderDraftError'), { autoClose: 3000 });
      console.error('Error adding to order draft:', error);
    }
  );
};
</script>
